import { z } from 'zod';
import { FINANCIAL_ENTRY_RECURRENCE, FINANCIAL_ENTRY_STATUS } from '../constants';
import { parseCurrencyValue } from '../financialFormatters';

const parseAmount = (value?: string): number | undefined => {
  const parsed = parseCurrencyValue(value);
  return parsed && parsed > 0 ? parsed : undefined;
};

const requiredOption = (message: string) =>
  z
    .number({ invalid_type_error: message })
    .nullable()
    .refine((value): value is number => value !== null, message);

const requiredId = (message: string) =>
  z
    .number({ invalid_type_error: message })
    .nullable()
    .refine((value): value is number => value !== null && value > 0, message);

const linkedUserSchema = z.object({
  id: z.number(),
  name: z.string(),
});

const installmentSchema = z.object({
  id: z.number().optional(),
  value: z.string().optional(),
  dueDate: z.string().optional(),
  commission: z.string().optional(),
  hasCommission: z.boolean().optional(),
  installmentNumber: z.string().optional(),
});

const itemSchema = z.object({
  id: z.number().optional(),
  businessUnit: z.string().optional(),
  project: z.string().optional(),
  competenceMonth: z.string().optional(),
  competenceYear: z.string().optional(),
  attachments: z.array(z.any()).optional(),
  pendingFiles: z.array(z.any()).optional(),
  pendingGalleryFiles: z.array(z.any()).optional(),
});

export const financialEntryFormSchema = z
  .object({
    payerCompanyId: requiredId('Selecione o pagador'),
    payerName: z.string().optional(),
    receiverCompanyId: requiredId('Selecione o recebedor'),
    receiverName: z.string().optional(),
    dreLineId: z.number().nullable().optional(),
    dreLineName: z.string().optional(),
    linkedUsers: z
      .array(linkedUserSchema)
      .min(1, 'Selecione o usuário vinculado'),
    type: requiredOption('Selecione o tipo'),
    category: requiredOption('Selecione a categoria'),
    subcategory: z.number().nullable().optional(),
    subcategoryName: z.string().optional(),
    status: requiredOption('Selecione o status'),
    recurrence: z.number().nullable().optional(),
    isRecurring: z.boolean().optional(),
    recurrenceStartDate: z.string().optional(),
    recurrenceDaysOfWeek: z.array(z.number()).optional(),
    recurrenceDueDays: z.array(z.number()).optional(),
    recurrenceAnnualDates: z.array(z.string()).optional(),
    recurrenceCount: z.string().optional(),
    description: z.string().optional(),
    paymentMethodId: requiredOption('Selecione a forma de pagamento'),
    paymentConditionId: z.number().nullable().optional(),
    paymentConditionName: z.string().optional(),
    totalValue: z.string().optional(),
    freight: z.string().optional(),
    icms: z.string().optional(),
    ipi: z.string().optional(),
    installments: z.array(installmentSchema).min(1, 'Informe ao menos uma parcela'),
    items: z.array(itemSchema).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.payerCompanyId != null &&
      data.receiverCompanyId != null &&
      data.payerCompanyId === data.receiverCompanyId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Pagador e recebedor devem ser empresas diferentes.',
        path: ['receiverCompanyId'],
      });
    }

    if (data.isRecurring) {
      if (!parseAmount(data.totalValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Informe o valor por ocorrência.',
          path: ['totalValue'],
        });
      }

      if (data.recurrence === null || data.recurrence === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Selecione a frequência da recorrência.',
          path: ['recurrence'],
        });
      }

      const isYearly = data.recurrence === FINANCIAL_ENTRY_RECURRENCE.YEARLY;
      const isWeekly = data.recurrence === FINANCIAL_ENTRY_RECURRENCE.WEEKLY;
      const isMonthly = data.recurrence === FINANCIAL_ENTRY_RECURRENCE.MONTHLY;

      if (!isYearly && !data.recurrenceStartDate?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Informe a data de início da recorrência.',
          path: ['recurrenceStartDate'],
        });
      }

      if (isWeekly && !(data.recurrenceDaysOfWeek?.length)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Selecione ao menos um dia da semana.',
          path: ['recurrenceDaysOfWeek'],
        });
      }

      if (isMonthly && !(data.recurrenceDueDays?.length)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Selecione ao menos um dia do mês.',
          path: ['recurrenceDueDays'],
        });
      }

      if (isYearly && !(data.recurrenceAnnualDates?.length)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Selecione ao menos uma data do ano.',
          path: ['recurrenceAnnualDates'],
        });
      }

      if (data.recurrenceCount?.trim()) {
        const count = Number(data.recurrenceCount);
        if (!Number.isInteger(count) || count <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Informe um número de ocorrências válido.',
            path: ['recurrenceCount'],
          });
        }
      }
    }

    // Sem recorrência as parcelas vêm da condição (30/60/90) sobre o valor
    // total, então o usuário preenche valor e condição em vez da grade.
    if (!data.isRecurring) {
      if (!parseAmount(data.totalValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Informe o valor total.',
          path: ['totalValue'],
        });
      }

      if (data.paymentConditionId == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Selecione a condição de pagamento.',
          path: ['paymentConditionId'],
        });
      }
    }
  });

export type FinancialEntryFormSchemaType = z.input<typeof financialEntryFormSchema>;

export type FinancialEntryFormInstallment =
  FinancialEntryFormSchemaType['installments'][number];

export type FinancialEntryFormItem = NonNullable<
  FinancialEntryFormSchemaType['items']
>[number];

export const defaultFormSchemaValues: FinancialEntryFormSchemaType = {
  payerCompanyId: null,
  payerName: '',
  receiverCompanyId: null,
  receiverName: '',
  dreLineId: null,
  dreLineName: '',
  linkedUsers: [],
  type: null,
  category: null,
  subcategory: null,
  subcategoryName: '',
  status: FINANCIAL_ENTRY_STATUS.PROVISIONED,
  recurrence: null,
  isRecurring: false,
  recurrenceStartDate: '',
  recurrenceDaysOfWeek: [],
  recurrenceDueDays: [],
  recurrenceAnnualDates: [],
  recurrenceCount: '',
  description: '',
  paymentMethodId: null,
  paymentConditionId: null,
  paymentConditionName: '',
  totalValue: '',
  freight: '',
  icms: '',
  ipi: '',
  installments: [
    {
      value: '',
      dueDate: '',
      commission: '',
      hasCommission: false,
      installmentNumber: '1/1',
    },
  ],
  items: [
    {
      businessUnit: '',
      project: '',
      competenceMonth: '',
      competenceYear: '',
      attachments: [],
      pendingFiles: [],
      pendingGalleryFiles: [],
    },
  ],
};
